import { Box, Typography, Button, Input, Snackbar } from '@mui/joy';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useAuth } from 'react-oidc-context';
import { FC, JSX, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useDeleteCompanyMutation,
  useGetCompaniesQuery,
  useGetEnterpriseOrgStatusQuery,
  useRetryProvisioningMutation,
} from '@consolidocs-ui/data-access/CompanyApi';
import { toEnterpriseOrgStatusList } from '@consolidocs-ui/hooks/useEnterpriseOrgStatus';
import { Loader, AlertModal } from '@consolidocs-ui/components';
import { isEnterpriseCompany } from './CompanyCard';
import CompanySection from './CompanySection';
import './Companies.scss';

const Companies: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { data: storedCompanies = [], isLoading } = useGetCompaniesQuery();
  // The organization list only carries the stored provisioning state, which
  // the backend refreshes from CodeBuild when this endpoint is read. Fetch it
  // once so cards show live status and a failed build offers a retry.
  const hasEnterprise = storedCompanies.some(isEnterpriseCompany);
  const { data: enterpriseStatusData } = useGetEnterpriseOrgStatusQuery(
    undefined,
    { skip: !hasEnterprise, refetchOnMountOrArgChange: true }
  );
  const companies = useMemo(() => {
    const statusById = new Map(
      toEnterpriseOrgStatusList(enterpriseStatusData).map((row) => [
        row.id,
        row,
      ])
    );
    if (!statusById.size) return storedCompanies;
    return storedCompanies.map((company) => {
      const live = company.id ? statusById.get(company.id) : undefined;
      return live
        ? {
            ...company,
            provisioning_status:
              live.provisioningStatus ?? company.provisioning_status,
            isDbCreated: live.isDbCreated,
            isCognitoPoolCreated: live.isCognitoPoolCreated,
            isSqsReady: live.isSqsReady,
            isServiceReady: live.isServiceReady,
          }
        : company;
    });
  }, [storedCompanies, enterpriseStatusData]);
  const auth = useAuth();
  const email = auth?.user?.profile.email || '';
  const rawName = email ? email.split('@')[0] : '';
  const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);
  const [deleteCompany, { isLoading: isDeleteMutationLoading }] =
    useDeleteCompanyMutation();
  const [companyIdToDelete, setCompanyIdToDelete] = useState<string | null>(
    null
  );
  const [retryProvisioning] = useRetryProvisioningMutation();
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    color: 'success' | 'danger';
  }>({
    open: false,
    message: '',
    color: 'success',
  });
  const triggerToast = (message: string, color: 'success' | 'danger') => {
    setToast({ open: true, message, color });
  };

  const openDeleteDialog = (id?: string) => setCompanyIdToDelete(id || null);
  const closeDeleteDialog = () => setCompanyIdToDelete(null);

  const handleDelete = async () => {
    if (!companyIdToDelete) return;

    try {
      await deleteCompany(companyIdToDelete).unwrap();
      setCompanyIdToDelete(null);
      triggerToast('Company deleted successfully', 'success');
    } catch (error) {
      triggerToast('Failed to delete company', 'danger');
      return;
    }
  };

  const handleRetryProvisioning = async (id?: string) => {
    if (!id || retryingId) return;
    setRetryingId(id);
    try {
      await retryProvisioning(id).unwrap();
      triggerToast('Infrastructure setup restarted', 'success');
    } catch (error: any) {
      triggerToast(
        error?.data?.status_message || 'Failed to restart infrastructure setup',
        'danger'
      );
    } finally {
      setRetryingId(null);
    }
  };

  const editCompany = (id?: string) => {
    if (id) {
      navigate(`/companies/edit/${id}`);
    }
  };

  const openCreateCompany = () => {
    navigate('/companies/create');
  };

  // Filter by name / email / description / industry, then group into their own
  // sections by type (newest first within each) so enterprise and general
  // companies are visually separated.
  const sections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? companies.filter((company) =>
          [
            company.name,
            company.email,
            company.description,
            company.industry_display?.name,
          ].some((field) => (field || '').toLowerCase().includes(query))
        )
      : companies;

    const sorted = [...filtered].sort(
      (a, b) =>
        new Date(b?.created_at || 'now').getTime() -
        new Date(a?.created_at || 'now').getTime()
    );

    return [
      {
        key: 'enterprise',
        title: 'Enterprise',
        color: 'primary' as const,
        items: sorted.filter(isEnterpriseCompany),
      },
      {
        key: 'general',
        title: 'General',
        color: 'neutral' as const,
        items: sorted.filter((company) => !isEnterpriseCompany(company)),
      },
    ].filter((section) => section.items.length > 0);
  }, [companies, searchQuery]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box className="companies-block">
      <Box className="header">
        <Typography level="h2" className="title">
          {`👋 Welcome ${name}`}
        </Typography>
        <Button
          variant="solid"
          onClick={openCreateCompany}
          color="primary"
          className="add-company-btn"
        >
          + Add New Company
        </Button>
      </Box>

      {companies.length > 0 && (
        <Box className="companies-search">
          <Input
            size="sm"
            placeholder="Search organizations…"
            startDecorator={<SearchRoundedIcon />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ maxWidth: 380 }}
          />
        </Box>
      )}

      {companies.length === 0 ? (
        <Box
          className="no-companies-found w-full bg-white rounded-lg h-[60vh] flex items-center justify-center flex-col"
          sx={{ textAlign: 'center', py: 5 }}
        >
          <Typography level="h4" sx={{ mb: 1 }}>
            🚫 No Companies Found
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            Click “+ Add New Company” to get started.
          </Typography>
        </Box>
      ) : sections.length === 0 ? (
        <Box
          className="no-companies-found w-full bg-white rounded-lg flex items-center justify-center flex-col"
          sx={{ textAlign: 'center', py: 6 }}
        >
          <Typography level="h4" sx={{ mb: 1 }}>
            🔍 No matches
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            No organizations match “{searchQuery}”.
          </Typography>
        </Box>
      ) : (
        sections.map((section) => (
          <CompanySection
            key={section.key}
            title={section.title}
            color={section.color}
            companies={section.items}
            onEdit={editCompany}
            onDelete={openDeleteDialog}
            onRetryProvisioning={handleRetryProvisioning}
            retryingId={retryingId}
          />
        ))
      )}

      <AlertModal
        alertAction={handleDelete}
        alertActionText="Delete"
        alertCancelText="Cancel"
        isLoading={isDeleteMutationLoading}
        alertMessage="Are you sure you want to delete this company?"
        alertTitle="Delete Company"
        open={!!companyIdToDelete}
        setOpen={() => closeDeleteDialog()}
      />

      {/* Toast Notification */}
      <Snackbar
        sx={{
          zIndex: '99999',
        }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        color={toast.color}
        variant="soft"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast.message}
      </Snackbar>
    </Box>
  );
};

export default Companies;
